// ThreeScene.jsx (snippet inside your component)
import { useEffect, useRef, useState } from 'react'
import { useShallow } from 'zustand/shallow';
import useEditorStore from '../../../stores/useEditorStore'
import usePreparingScanStore from '../../../stores/usePreparingScanStore'
import * as THREE from 'three'
import { computePlaneFromLine } from '../../../utils/slicing';
import { TOOLS_CONFIG } from '../../../constants/tools';
import useSliceStore from '../../../stores/tool/useSliceStore';
import { SlicingManager } from './SlicingManager';
import { FillToolManager } from './FillToolManager';
import { pickAnywhereOnCanvas } from '../../tools/helpers';

const PreparingScanHandler = () => {

  const [activeTool] = useEditorStore(useShallow(
    (state) => [
      state.activeTool
    ]
  ))
  const sceneRefs = useEditorStore(s => s.sceneRefs)
  const [selectionPoints, selectedRegion, updatePoint, setIsDraggingPoint, isDraggingPoint] = usePreparingScanStore(
    useShallow((state) => [state.selectionPoints, state.selectedRegion, state.updatePoint, state.setIsDraggingPoint, state.isDraggingPoint])
  );
  const container = useRef([])   // holds current helper meshes
  const [draggingIndex, setDraggingIndex] = useState(null) // tracks which point is being dragged
  const raycaster = useRef(new THREE.Raycaster())
  const mouse = useRef(new THREE.Vector2())

  useEffect(() => {
    if(!sceneRefs || !sceneRefs.scene) return;
    const { scene, controls } = sceneRefs

    // 1) Remove old helpers
    container.current.forEach(obj => scene.remove(obj))
    container.current = []

    // 2) Spawn spheres for each point
    const mat = new THREE.MeshBasicMaterial({ color: 0x0000ff, depthTest: false })
    const meshScale = sceneRefs.shadedMesh ? sceneRefs.shadedMesh.scale.x : 1;
    selectionPoints.forEach((pt, index) => {
      const sph = new THREE.Mesh(
        new THREE.SphereGeometry(0.5 * meshScale, 16, 16),
        mat
      )
      sph.renderOrder = 2;
      sph.position.copy(pt)
      sph.userData.pointIndex = index; // Store which point this sphere represents
      scene.add(sph)
      container.current.push(sph)
    })

    // 3) If two points, draw a line
    if (selectionPoints.length === 2) {
      const [a, b] = selectionPoints
      const lineGeo = new THREE.BufferGeometry().setFromPoints([a, b])
      const lineMat = new THREE.LineBasicMaterial({ color: 0x583ca3, linewidth: 20, depthTest: false })
      const line = new THREE.Line(lineGeo, lineMat)
      scene.add(line)
      container.current.push(line)

      // Only compute slicing plane if not currently dragging
      if (!isDraggingPoint) {
        const camera = useEditorStore.getState().sceneRefs.camera;
        const plane = computePlaneFromLine(a, b, camera);
        const sliceStore = useSliceStore.getState();

        // Use startSlicePreview only if no plane exists yet (initial placement)
        // Otherwise use updateSlicingPlane to preserve selection direction
        if (sliceStore.slicingPlane === null) {
          sliceStore.startSlicePreview(plane);
        } else {
          sliceStore.updateSlicingPlane(plane);
        }
      }

      // Disable orbit controls after two points are placed
      if (controls) {
        controls.enabled = false;
      }
    } else {
      // Re-enable orbit controls if less than two points
      if (controls) {
        controls.enabled = true;
      }
    }

    // cleanup on unmount
    return () => {
      container.current.forEach(obj => scene.remove(obj))
      container.current = []
      // Re-enable controls on cleanup
      if (controls) {
        controls.enabled = true;
      }
    }
  }, [selectionPoints, isDraggingPoint]);

  // Effect to handle point dragging
  useEffect(() => {
    if (!sceneRefs || !sceneRefs.renderer || activeTool !== TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.TRIM.id) return;
    if (selectionPoints.length !== 2) return; // Only enable dragging when we have 2 points

    const canvas = sceneRefs.renderer.domElement;
    const { camera } = sceneRefs;

    const handlePointerDown = (event) => {
      if (event.button !== 0) return; // Only left click

      const rect = canvas.getBoundingClientRect();
      mouse.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      raycaster.current.setFromCamera(mouse.current, camera);

      // Only check the point spheres (first two objects in container)
      const pointSpheres = container.current.filter(obj => obj.isMesh && obj.userData.pointIndex !== undefined);
      const intersects = raycaster.current.intersectObjects(pointSpheres);

      if (intersects.length > 0) {
        const pointIndex = intersects[0].object.userData.pointIndex;
        setDraggingIndex(pointIndex);
        setIsDraggingPoint(true);
        canvas.style.cursor = 'grabbing';
      }
    };

    const handlePointerMove = (event) => {
      if (draggingIndex === null) return;

      // Pick a new position on the virtual plane
      const newPoint = pickAnywhereOnCanvas(event, sceneRefs);
      if (newPoint) {
        updatePoint(draggingIndex, newPoint);
      }
    };

    const handlePointerUp = () => {
      if (draggingIndex !== null) {
        setDraggingIndex(null);
        setIsDraggingPoint(false);
        canvas.style.cursor = 'auto';

        // Trigger slicing plane update on drag end (preserves selection direction)
        if (selectionPoints.length === 2) {
          const [a, b] = selectionPoints;
          const camera = useEditorStore.getState().sceneRefs.camera;
          const plane = computePlaneFromLine(a, b, camera);
          useSliceStore.getState().updateSlicingPlane(plane);
        }
      }
    };

    canvas.addEventListener('pointerdown', handlePointerDown);
    canvas.addEventListener('pointermove', handlePointerMove);
    canvas.addEventListener('pointerup', handlePointerUp);

    return () => {
      canvas.removeEventListener('pointerdown', handlePointerDown);
      canvas.removeEventListener('pointermove', handlePointerMove);
      canvas.removeEventListener('pointerup', handlePointerUp);
    };
  }, [sceneRefs, activeTool, selectionPoints, draggingIndex, updatePoint, setIsDraggingPoint]);

  return (
    <div className='w-full h-full'>
      {activeTool == TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.TRIM.id && <SlicingManager/>}
      {activeTool == TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.FILL.id && <FillToolManager/>}
    </div>
  )
}

export default PreparingScanHandler;  